import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthGuidedJourneyListItem, GrowthJourneyService } from './growth-journey.service';
import {
  GUIDED_JOURNEY_STATUS_I18N_KEYS,
  growthJourneyStepI18nKey,
  guidedJourneyStatusSeverity as resolveGuidedJourneyStatusSeverity,
  normalizeGuidedJourneyStatus
} from './growth-status.util';

@Component({
  standalone: true,
  selector: 'ain-growth-journey-list',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    TagModule,
    AinDatePipe,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.journey.guided.title')"
      [description]="t('growth.journey.guided.list.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.journey.guided.help.title'"
        [descriptionKey]="'growth.journey.guided.help.description'"
        [bulletKeys]="['growth.journey.guided.help.bullets.progress']" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>

        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (error()) {
          <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
        } @else if (items().length === 0) {
          <ain-state type="empty" [title]="t('growth.journey.guided.list.empty')" />
        } @else {
          <p-table [value]="items()" [rows]="10" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.journey.guided.list.columns.title') }}</th>
                <th>{{ t('growth.journey.guided.list.columns.status') }}</th>
                <th>{{ t('growth.journey.guided.list.columns.currentStep') }}</th>
                <th>{{ t('growth.journey.guided.list.columns.createdAt') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>
                  <a [routerLink]="['/growth/journey/journeys', row.id]" class="row-link">{{ row.title }}</a>
                </td>
                <td>
                  <p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)" />
                </td>
                <td>{{ t(stepLabelKey(row.currentStep)) }}</td>
                <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
                <td class="actions">
                  <a
                    pButton
                    size="small"
                    [routerLink]="['/growth/journey/journeys', row.id]"
                    [label]="t('common.open')"></a>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .row-link { color: inherit; font-weight: 600; text-decoration: none; }
    .row-link:hover { text-decoration: underline; }
  `]
})
export class GrowthJourneyListComponent implements OnInit {
  private readonly journey = inject(GrowthJourneyService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<GrowthGuidedJourneyListItem[]>([]);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.journey.listJourneys().subscribe({
      next: items => {
        this.items.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  statusLabel(status: unknown): string {
    const normalized = normalizeGuidedJourneyStatus(status);
    return this.t(GUIDED_JOURNEY_STATUS_I18N_KEYS[normalized]);
  }

  statusSeverity(status: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveGuidedJourneyStatusSeverity(status);
  }

  stepLabelKey(step: unknown): string {
    return growthJourneyStepI18nKey(step);
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
